import { nearestSegmentHit } from "./collision-ray.js";
import { findMapHeightLayer } from "./radar-transform.js";
const RAY_COUNT = 72;
const MOLLY_STEP_HU = 8;
const MAX_MOLLY_STEP_HEIGHT_HU = 18;
const VISIBILITY_STEP_HU = 96;
const EPSILON = 1e-6;
/**
 * Nominale CS2-Wirkradien in Hammer Units; die Map-Geometrie schneidet sie zu.
 *
 * - FLASH: absoluter Engine-Suchradius aus `RadiusFlash()` (CS:GO-2020-Source-Leak,
 *   `flashbang_projectile.cpp`; CS2 übernimmt die Granatenmechanik unverändert).
 *   Echte Blendung ist KEIN reiner Radius, sondern hängt pro Spieler zusätzlich von
 *   Sichtlinie und Blickwinkel (Dot-Product zur Blickrichtung) ab — dieser Wert ist
 *   der Punkt, ab dem die Engine niemanden mehr prüft, nicht "hier ist jeder blind".
 *   Ein Per-Spieler-Blickwinkelmodell existiert hier bewusst nicht.
 * - HE / SMOKE: Community-/Wiki-Konsenswerte (Liquipedia/Fandom Counter-Strike Wiki).
 * - MOLLY: unverändert gelassen — kein Engine-Wert auffindbar, Ausbreitung ist
 *   terrainabhängig, ein einfacher Radius bildet das ohnehin nur grob ab.
 * - DECOY: keine Engine-Konstante auffindbar (spielt normalen Waffensound ab, real
 *   praktisch map-weit hörbar); 2000 ist eine bewusste, unbelegte Näherung.
 */
export const grenadeEffectRadiusHu = {
    SMOKE: 144,
    MOLLY: 150,
    FLASH: 3000,
    HE: 384,
    DECOY: 2000,
};
function subtract(left, right) {
    return {
        x: left.x - right.x,
        y: left.y - right.y,
        z: left.z - right.z,
    };
}
function cross(left, right) {
    return {
        x: left.y * right.z - left.z * right.y,
        y: left.z * right.x - left.x * right.z,
        z: left.x * right.y - left.y * right.x,
    };
}
function candidateTriangleIndexes(collision, start, end) {
    const ranges = collision.queryRanges({
        x: Math.min(start.x, end.x),
        y: Math.min(start.y, end.y),
        z: Math.min(start.z, end.z),
    }, {
        x: Math.max(start.x, end.x),
        y: Math.max(start.y, end.y),
        z: Math.max(start.z, end.z),
    });
    const indexes = new Set();
    for (const range of ranges) {
        for (let index = range.startTriangle; index < range.startTriangle + range.triangleCount; index += 1) {
            indexes.add(index);
        }
    }
    return [...indexes];
}
function nearestCollisionFraction(collision, start, end, wallsOnly = false) {
    // Kontakt direkt am Ursprung ist der Boden, auf dem die Granate liegt.
    return nearestSegmentHit(collision, start, end, {
        ignoreUpTo: 0.01,
        wallsOnly,
    });
}
function pointInTriangle2d(x, y, [a, b, c]) {
    const denominator = (b.y - c.y) * (a.x - c.x) + (c.x - b.x) * (a.y - c.y);
    if (Math.abs(denominator) <= EPSILON)
        return false;
    const first = ((b.y - c.y) * (x - c.x) + (c.x - b.x) * (y - c.y)) / denominator;
    const second = ((c.y - a.y) * (x - c.x) + (a.x - c.x) * (y - c.y)) / denominator;
    const third = 1 - first - second;
    return first >= -EPSILON && second >= -EPSILON && third >= -EPSILON;
}
function triangleHeightAt(x, y, vertices) {
    if (!pointInTriangle2d(x, y, vertices))
        return null;
    const [a, b, c] = vertices;
    const normal = cross(subtract(b, a), subtract(c, a));
    if (Math.abs(normal.z) < 0.35)
        return null;
    return a.z - (normal.x * (x - a.x) + normal.y * (y - a.y)) / normal.z;
}
function selectedLayer(layers, preferredLayerId) {
    if (preferredLayerId) {
        const preferred = layers.find((layer) => layer.id === preferredLayerId);
        if (preferred)
            return preferred;
    }
    return layers[0] ?? null;
}
/** Höchste begehbare Fläche der gewählten Kartenebene an einer Weltposition. */
export function findGroundHeight(collision, layers, x, y, preferredLayerId) {
    const layer = selectedLayer(layers, preferredLayerId);
    const minimumZ = layer?.minZHu ?? collision.header.boundsHu.min[2];
    const maximumZ = layer?.maxZHu ?? collision.header.boundsHu.max[2];
    const indexes = candidateTriangleIndexes(collision, { x, y, z: minimumZ }, { x, y, z: maximumZ });
    let highest = null;
    for (const index of indexes) {
        const height = triangleHeightAt(x, y, collision.readTriangle(index).vertices);
        if (height === null ||
            height < minimumZ - EPSILON ||
            height > maximumZ + EPSILON) {
            continue;
        }
        if (highest === null || height > highest)
            highest = height;
    }
    return highest === null ? null : { z: highest, layerId: layer?.id ?? "main" };
}
function flatVisibleDistance(collision, origin, angle, radius) {
    const start = { ...origin, z: origin.z + 16 };
    const end = {
        x: origin.x + Math.cos(angle) * radius,
        y: origin.y + Math.sin(angle) * radius,
        z: start.z,
    };
    const hit = nearestCollisionFraction(collision, start, end);
    return hit === null ? radius : Math.max(0, radius * hit - 4);
}
/**
 * Terrain-nachgeführter Fallback für `flatVisibleDistance`: marschiert in
 * `VISIBILITY_STEP_HU`-Schritten und fragt pro Schritt die Bodenhöhe neu ab,
 * statt auf fester absoluter Höhe zu bleiben. Ein einzelner flacher Strahl
 * fliegt an erhöhten Stegen/Balkonen (z. B. Mirage "Arches") über die dort
 * tiefer liegende Wand hinweg und meldet fälschlich "frei". Anders als
 * `mollyDistance` bricht dieser Marsch NICHT bei starken Höhensprüngen ab —
 * Sichtlinien für FLASH/HE/SMOKE/DECOY dürfen Kanten und Abgründe queren, nur
 * echte feste Geometrie soll stoppen. Fehlt an einem Schritt eine Bodenfläche
 * (Lücke), bleibt die letzte bekannte Höhe erhalten, statt die Sichtlinie
 * künstlich abzubrechen.
 *
 * Der Wandtreffertest ignoriert bodenartige Dreiecke (`wallsOnly`): das
 * nachgeführte Segment zwischen zwei Bodenhöhen-Samples verläuft diagonal und
 * schneidet an Kanten/Absätzen sonst die eigene Ausgangsfläche selbst — ohne
 * den Filter würde jede Geländestufe fälschlich als Wandtreffer erkannt.
 */
function terrainConformingVisibleDistance(collision, layers, layerId, origin, angle, radius) {
    let previous = { ...origin, z: origin.z + 16 };
    let distance = 0;
    while (distance < radius) {
        const nextDistance = Math.min(distance + VISIBILITY_STEP_HU, radius);
        const x = origin.x + Math.cos(angle) * nextDistance;
        const y = origin.y + Math.sin(angle) * nextDistance;
        const ground = findGroundHeight(collision, layers, x, y, layerId);
        const z = ground ? ground.z + 16 : previous.z;
        const next = { x, y, z };
        const hit = nearestCollisionFraction(collision, previous, next, true);
        if (hit !== null) {
            const stepLength = nextDistance - distance;
            return Math.max(0, distance + stepLength * hit - 4);
        }
        distance = nextDistance;
        previous = next;
    }
    return distance;
}
function visibleDistance(collision, layers, layerId, origin, angle, radius) {
    const flat = flatVisibleDistance(collision, origin, angle, radius);
    // Nur bei "komplett frei" nachprüfen: das ist exakt der Fall, in dem ein
    // flacher Strahl über eine tiefer liegende Wand hinwegfliegt (erhöhte
    // Wege/Balkone, siehe Mirage "Arches"). Ein echter Treffer ist unabhängig
    // von der Höhe korrekt und muss nicht erneut geprüft werden.
    if (flat < radius)
        return flat;
    return terrainConformingVisibleDistance(collision, layers, layerId, origin, angle, radius);
}
function mollyDistance(collision, layers, layerId, origin, angle, radius) {
    let distance = 0;
    let previous = origin;
    for (let nextDistance = MOLLY_STEP_HU; nextDistance <= radius; nextDistance += MOLLY_STEP_HU) {
        const x = origin.x + Math.cos(angle) * nextDistance;
        const y = origin.y + Math.sin(angle) * nextDistance;
        const ground = findGroundHeight(collision, layers, x, y, layerId);
        if (!ground || Math.abs(ground.z - previous.z) > MAX_MOLLY_STEP_HEIGHT_HU) {
            break;
        }
        const next = { x, y, z: ground.z };
        const wallHit = nearestCollisionFraction(collision, { ...previous, z: previous.z + 12 }, { ...next, z: next.z + 12 });
        if (wallHit !== null)
            break;
        distance = nextDistance;
        previous = next;
    }
    return distance;
}
/**
 * Erzeugt drei distanzgewichtete, durch die echte Map-Geometrie begrenzte
 * Polygone. Die Ausgabe ist rendererunabhängig und snapshot-tauglich.
 */
export function generateGrenadeEffect(options) {
    const radius = grenadeEffectRadiusHu[options.grenadeType];
    const ground = options.snapToGround === false
        ? null
        : findGroundHeight(options.collision, options.layers, options.origin.x, options.origin.y, options.layerId);
    const layerId = ground?.layerId ??
        options.layerId ??
        findMapHeightLayer(options.layers, options.origin.z)?.id ??
        options.layers[0]?.id ??
        "main";
    const origin = ground ? { ...options.origin, z: ground.z } : options.origin;
    const maximumDistances = Array.from({ length: RAY_COUNT }, (_, index) => {
        const angle = (index / RAY_COUNT) * Math.PI * 2;
        return options.grenadeType === "MOLLY"
            ? mollyDistance(options.collision, options.layers, layerId, origin, angle, radius)
            : visibleDistance(options.collision, options.layers, layerId, origin, angle, radius);
    });
    const bands = [
        { factor: 1, intensity: 0.25 },
        { factor: 0.67, intensity: 0.6 },
        { factor: 0.34, intensity: 1 },
    ];
    return {
        grenadeType: options.grenadeType,
        ruleset: options.ruleset,
        mapAsset: options.mapAsset,
        origin,
        startedAtSeconds: options.startedAtSeconds,
        areas: bands.map((band) => ({
            layerId,
            intensity: band.intensity,
            boundary: maximumDistances.map((maximumDistance, index) => {
                const angle = (index / RAY_COUNT) * Math.PI * 2;
                const distance = Math.min(maximumDistance, radius * band.factor);
                return {
                    x: origin.x + Math.cos(angle) * distance,
                    y: origin.y + Math.sin(angle) * distance,
                    z: origin.z,
                };
            }),
        })),
        approximation: "collision-mesh-radial-v1",
    };
}
